// The photographs for a SAFed visit (Session BDB, Part 3), on the existing job photo path: each goes through the offline outbox
// (queuePhoto) and uploads when there is a signal, exactly as an ordinary job's photos do. Six at most; the seventh is refused here
// and, if one ever reached it, by the server. Optional prompts are suggestions: they never block a test. No location is requested
// and no photograph is described as taken at a time or place we were not told.
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera } from "lucide-react";
import { queuePhoto, queuedPhotosForJob } from "../../lib/offline/photosStore";
import { isImageFile, prepareImageForUpload } from "../../lib/imageUpload";
import { SAFED_PHOTO_CAP, SAFED_PHOTO_CAP_MESSAGE } from "../../lib/safedPhotos";
import { useEngineerData } from "./EngineerDataContext";

interface Tile {
  id: string;
  previewUrl: string;
  lastError?: string;
}

export function SafedPhotos({ jobId, prompts = [] }: { jobId: string; prompts?: string[] }) {
  const { jobById, online, syncNow } = useEngineerData();
  const uploaded = jobById(jobId)?.photos ?? [];
  const [queued, setQueued] = useState<Tile[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const urls = useRef<string[]>([]);
  const input = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    const items = await queuedPhotosForJob(jobId);
    const tiles = items.map((it) => ({ id: it.id, previewUrl: URL.createObjectURL(it.blob), lastError: it.lastError }));
    setQueued((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      urls.current = tiles.map((t) => t.previewUrl);
      return tiles;
    });
  }, [jobId]);

  useEffect(() => {
    void reload();
    return () => {
      urls.current.forEach((u) => URL.revokeObjectURL(u));
      urls.current = [];
    };
  }, [reload]);

  useEffect(() => {
    if (online) void reload();
  }, [online, reload]);

  const count = uploaded.length + queued.length;

  async function add(selected: File[]) {
    let files = selected.filter(isImageFile);
    if (files.length === 0) return;
    setMessage(null);
    const room = SAFED_PHOTO_CAP - count;
    if (room <= 0) {
      setMessage(SAFED_PHOTO_CAP_MESSAGE);
      return;
    }
    if (files.length > room) {
      files = files.slice(0, room);
      setMessage(`A visit holds up to ${SAFED_PHOTO_CAP} photos, so only the first ${room} were added.`);
    }
    setBusy(true);
    try {
      for (const original of files) {
        const f = await prepareImageForUpload(original);
        await queuePhoto(jobId, f, f.type);
      }
      await reload();
      if (navigator.onLine) {
        await syncNow();
        await reload();
      }
    } catch {
      setMessage("Could not save a photo. It is kept on this phone and will try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="Photos for this visit" className="mt-4 rounded-ele border border-ele-border bg-ele-card p-3">
      <h3 className="font-heading text-base text-ele-strong">Photos ({count} of {SAFED_PHOTO_CAP})</h3>
      {prompts.length > 0 && (
        <p className="mt-1 text-sm text-ele-muted">
          If you can: {prompts.join("; ")}. These are optional and never stop you completing a test.
        </p>
      )}
      {count > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-2">
          {uploaded.map((_, i) => (
            <div key={`up-${i}`} className="flex aspect-square items-center justify-center rounded-ele border border-ele-border bg-ele-inset text-xs text-ele-muted">
              Photo {i + 1} sent
            </div>
          ))}
          {queued.map((t) => (
            <div key={t.id} className="relative aspect-square overflow-hidden rounded-ele border border-ele-border bg-ele-inset">
              <img src={t.previewUrl} alt="Photo waiting to send" className="h-full w-full object-cover opacity-80" />
              <span className="absolute bottom-0 left-0 right-0 bg-black/70 py-0.5 text-center text-[10px] uppercase text-[#CACACA]">
                {t.lastError ? "Will retry" : online ? "Sending" : "Queued"}
              </span>
            </div>
          ))}
        </div>
      )}
      {message && <p role="status" className="mt-2 text-sm text-amber-400">{message}</p>}
      <input
        ref={input}
        id={`safed-photo-${jobId}`}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (input.current) input.current.value = "";
          void add(files);
        }}
      />
      {count >= SAFED_PHOTO_CAP ? (
        <p className="mt-3 text-center font-menu text-xs uppercase tracking-wide text-ele-muted">Maximum of {SAFED_PHOTO_CAP} photos reached</p>
      ) : (
        <label
          htmlFor={`safed-photo-${jobId}`}
          className={`mt-3 flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-ele-border font-heading text-base text-ele-strong ${busy ? "opacity-50" : ""}`}
        >
          <Camera className="h-5 w-5" aria-hidden="true" /> Add photo
        </label>
      )}
    </section>
  );
}
